import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { authMe, authLogout } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  // Evaluated synchronously at render time — BEFORE any effects fire.
  // This is the only reliable way to check oauth_mode before AppRouter's
  // effect removes it (children effects run before parent effects).
  const skipAuthCheckRef = useRef(
    window.location.pathname === '/auth/callback' ||
    window.location.hash.includes('access_token=') ||
    window.location.hash.includes('session_id=') ||
    window.location.hash.includes('error=') ||
    !!localStorage.getItem('oauth_mode')
  );

  const [loading, setLoading] = useState(skipAuthCheckRef.current ? false : true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authMe();
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipAuthCheckRef.current) return;
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await authLogout();
    } catch { /* ignore */ }
    setUser(null);
  };

  const login = useCallback((userData) => {
    setUser(userData);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
